import { useState } from 'react'
import { useCookies } from 'react-cookie'
import { useQuery } from '@tanstack/react-query'
import Image from 'next/image'

import NetsLogo from '../../../../public/nets-logo.png'
import { getNetsCheckout } from '@/lib/service-api'
import { trackBeginCheckout } from '@/lib/tracking'
import { isOrderDisabled } from '@/lib/utils'
import type { CheckoutModelInput } from '@/service-api/types.generated'
import { useBillingAddress, useDeliveryAddress } from '@/stores/useAddressStore'
import { TermsAndConditions } from '@/app/[locale]/(common)/terms-and-conditions'
import { useBasket } from '@/app/[locale]/(basket)/index'
import { Alert } from '@/app/[locale]/(ui)/alert'
import type { ApiAddressSuggestion } from '@/types/address-suggestions'
import { ICustomer } from '@/types/icustomer'
import { Button } from '@/app/[locale]/(ui)/button'
import { useRouter } from '@/i18n/navigation'
import { useTranslations } from 'next-intl'

export const NetsPayment = ({ checkoutModel }: { checkoutModel: CheckoutModelInput }) => {
  const [cookies] = useCookies(['user'])
  const { cart } = useBasket()
  const router = useRouter()
  const t = useTranslations()
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false)
  const [isPaymentLoading, setIsPaymentLoading] = useState(false)
  const deliveryAddress = useDeliveryAddress()
  const billingAddress = useBillingAddress()
  const [error, setError] = useState('')
  const { user: customer } = cookies
  const hasError = !!error

  const isComplete = ({
    customer,
    billingAddress,
    deliveryAddress,
  }: {
    customer?: ICustomer
    billingAddress?: ApiAddressSuggestion
    deliveryAddress?: ApiAddressSuggestion
  }) => {
    if (!customer) return false
    const userFields = [customer.email, customer.firstName, customer.phone, billingAddress, deliveryAddress]
    return userFields.every((field) => !!field)
  }

  const { refetch } = useQuery({
    queryKey: ['nets-checkout', checkoutModel],
    queryFn: async () => {
      const response = await getNetsCheckout(checkoutModel)

      if (!response.paymentProviders?.nets?.createPayment) {
        throw new Error('Failed_load_Nets')
      }

      return response
    },
    // This query is disabled by default and only runs when manually triggered.
    // This prevents unnecessary API calls on component mount or checkoutModel changes.
    // The query is manually triggered in the handlePaymentClick function.
    enabled: false,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    onSuccess: (data) => {
      setIsPaymentLoading(false)
      const { success, paymentUrl } = data.paymentProviders?.nets?.createPayment || {}
      if (success && paymentUrl) {
        router.push(paymentUrl)
      } else {
        setError('Failed_load_Nets')
      }
    },
    onError: () => {
      setIsPaymentLoading(false)
      setError('Failed_load_Nets')
    },
  })

  const handlePaymentClick = () => {
    if (isPaymentLoading) return

    if (
      !isComplete({
        customer,
        deliveryAddress,
        billingAddress,
      })
    ) {
      setError('Please_fill_out_form')
      return
    }

    setIsPaymentLoading(true)
    trackBeginCheckout({ cart })
    refetch()
  }

  const isFormComplete = isComplete({
    customer,
    deliveryAddress,
    billingAddress,
  })
  const disabled = isPaymentLoading || isOrderDisabled.disableButton || !isFormComplete || !hasAcceptedTerms

  return (
    <>
      {hasError && <Alert variant="error">{t(error)}</Alert>}
      <div className="flex w-full flex-col gap-2">
        <TermsAndConditions className="mt-4" checked={hasAcceptedTerms} onChange={setHasAcceptedTerms} />
        <Button variant="primary" type="button" onClick={handlePaymentClick} disabled={disabled} className="relative">
          <div className="absolute bottom-0 left-0 top-0 flex items-center justify-center rounded-l-full border border-black bg-white p-2">
            <Image src={NetsLogo} alt="" width={56} height={24} className="object-contain" />
          </div>
          <div>{isPaymentLoading ? t('Processing_payment') : t('Pay_with_Nets')}</div>
        </Button>
      </div>
    </>
  )
}
